import React, { useState } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import {
  LayoutDashboard, FileText, ClipboardCheck, Scale, ShoppingCart, PackageCheck,
  FileCheck2, Wallet, Users, Database, ScrollText, Search, Sun, Moon, LogOut,
  Menu, X, ChevronRight, Building2, SlidersHorizontal
} from "lucide-react";
import { Button } from "@/components/ui/button";
import GlobalSearch from "@/components/GlobalSearch";

const ALL_NAV = [
  { to: "/", icon: LayoutDashboard, label: "Dashboard", testid: "nav-dashboard" },
  { to: "/indents", icon: FileText, label: "Indent Requirement & Requisitions", testid: "nav-indents" },
  { to: "/pos", icon: ShoppingCart, label: "Purchase Orders", testid: "nav-pos" },
  { to: "/grns", icon: PackageCheck, label: "Goods Receipt Notes", testid: "nav-grns" },
  { to: "/invoices", icon: FileCheck2, label: "Invoices", testid: "nav-invoices", module: "invoices" },
  { to: "/pvs", icon: Wallet, label: "Payment Vouchers", testid: "nav-pvs", module: "pvs" },
  { to: "/tally", icon: FileCheck2, label: "Tally Sync", testid: "nav-tally", adminOnly: true },
  { divider: true },
  { to: "/masters", icon: Database, label: "Masters", testid: "nav-masters" },
  { to: "/users", icon: Users, label: "Users", testid: "nav-users", adminOnly: true },
  { to: "/settings/visibility", icon: SlidersHorizontal, label: "Field Visibility", testid: "nav-settings", adminOnly: true },
  { to: "/audit", icon: ScrollText, label: "Audit Log", testid: "nav-audit", module: "audit", adminOnly: true },
];

export default function Layout() {
  const { user, logout, moduleHidden, hasRole } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [dark, setDark] = useState(() => localStorage.getItem("kipl_dark") === "1");
  const [open, setOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);

  const NAV = ALL_NAV.filter((n) => {
    if (n.divider) return true;
    if (n.module && moduleHidden(n.module)) return false;
    if (n.adminOnly && !hasRole("Administrator")) return false;
    return true;
  });

  React.useEffect(() => {
    if (dark) document.documentElement.classList.add("dark");
    else document.documentElement.classList.remove("dark");
    localStorage.setItem("kipl_dark", dark ? "1" : "0");
  }, [dark]);

  const handleLogout = async () => {
    await logout();
    nav("/login");
  };

  return (
    <div className="min-h-screen flex bg-background text-foreground">
      {/* Sidebar */}
      <aside
        data-testid="sidebar"
        className={`fixed lg:sticky top-0 z-40 h-screen w-72 sidebar-bg border-r border-white/5 transform transition-transform duration-200 flex flex-col ${open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}
      >
        <div className="px-6 py-5 flex items-center gap-3 border-b border-white/5">
          <div className="w-9 h-9 rounded-md bg-kabir grid place-items-center text-zinc-900 font-bold text-lg font-display">
            K
          </div>
          <div className="flex-1">
            <div className="font-display font-bold text-lg leading-tight">KABIR<span className="text-kabir"> INFRA</span></div>
            <div className="text-[10px] tracking-widest text-white/50 uppercase">Procurement ERP</div>
          </div>
          <button className="lg:hidden text-white/70" onClick={() => setOpen(false)} data-testid="sidebar-close">
            <X size={18} />
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-1">
          {NAV.map((n, i) => n.divider ? (
            <div key={i} className="my-3 h-px bg-white/5 mx-2" />
          ) : (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === "/"}
              data-testid={n.testid}
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-all ${
                  isActive
                    ? "bg-white/10 text-white font-semibold border-l-2 border-kabir"
                    : "text-white/70 hover:text-white hover:bg-white/5"
                }`
              }
            >
              <n.icon size={16} strokeWidth={2} />
              <span>{n.label}</span>
              <ChevronRight size={14} className="ml-auto opacity-30" />
            </NavLink>
          ))}
        </nav>

        <div className="p-3 border-t border-white/5">
          <div className="px-3 py-2 rounded-md bg-white/5 flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-kabir text-zinc-900 grid place-items-center font-bold text-sm">
              {(user?.name || "?").slice(0, 1).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate" data-testid="user-name">{user?.name}</div>
              <div className="text-[10px] text-white/60 truncate">{user?.roles?.join(", ")}</div>
            </div>
            <button
              onClick={handleLogout}
              data-testid="logout-btn"
              className="text-white/60 hover:text-white p-1.5 rounded hover:bg-white/10"
              title="Logout"
            >
              <LogOut size={15} />
            </button>
          </div>
        </div>
      </aside>

      {/* Overlay for mobile */}
      {open && <div className="fixed inset-0 bg-black/40 z-30 lg:hidden" onClick={() => setOpen(false)} />}

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="sticky top-0 z-20 bg-background/90 backdrop-blur border-b border-border px-4 lg:px-8 h-14 flex items-center gap-4">
          <button className="lg:hidden" onClick={() => setOpen(true)} data-testid="sidebar-open">
            <Menu size={20} />
          </button>
          <button
            onClick={() => setSearchOpen(true)}
            data-testid="global-search-trigger"
            className="flex-1 max-w-xl flex items-center gap-2 px-3 py-1.5 rounded-md border border-border bg-secondary/50 text-sm text-muted-foreground hover:border-kabir transition-colors"
          >
            <Search size={14} />
            <span>Search across indents, POs, vendors…</span>
            <kbd className="ml-auto text-xs px-1.5 py-0.5 rounded bg-background border border-border font-mono">⌘K</kbd>
          </button>
          <div className="ml-auto flex items-center gap-2">
            <div className="hidden md:block text-[10px] uppercase tracking-widest text-muted-foreground font-medium">
              <Building2 size={14} className="inline mr-1 -mt-0.5" />
              FY {new Date().getMonth() < 3 ? new Date().getFullYear() - 1 : new Date().getFullYear()}-{(new Date().getMonth() < 3 ? new Date().getFullYear() : new Date().getFullYear() + 1).toString().slice(2)}
            </div>
            <Button
              variant="ghost" size="icon"
              onClick={() => setDark((d) => !d)}
              data-testid="theme-toggle"
              className="h-8 w-8"
            >
              {dark ? <Sun size={16} /> : <Moon size={16} />}
            </Button>
          </div>
        </header>

        <main className="flex-1 p-4 lg:p-8 animate-fade-in-up" key={loc.pathname}>
          <Outlet />
        </main>
      </div>

      <GlobalSearch open={searchOpen} setOpen={setSearchOpen} />
    </div>
  );
}
